import { Box, HTMLChakraProps, Skeleton, SkeletonText } from '@chakra-ui/react';
import { useResource, useResources } from 'datx-jsonapi-react';
import { FC } from 'react';

import { ExternalAAA } from '@/components/ui/shared/event-ticket/ExternalAAA/ExternalAAA';
import { ExternalAAPT } from '@/components/ui/shared/event-ticket/ExternalAAPT/ExternalAAPT';
import { RegistrationFee } from '@/components/ui/shared/event-ticket/RegistrationFee/RegistrationFee';
import { SelfRegistered } from '@/components/ui/shared/event-ticket/SelfRegistered/SelfRegistered';
import { useQuery } from '@tanstack/react-query';

import { EventRegistrationType } from '@/enums/EventRegistrationType';
import { queryEventTicketForUser } from '@/queries/event-tickets';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { queryThinEvent } from '@/queries/thin-events';

export interface IEventTicketPromptProps extends HTMLChakraProps<'div'> {
  eventId: string;
}

export const EventTicketPrompt: FC<IEventTicketPromptProps> = ({ eventId, ...props }) => {
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;

  const { data: event, error: eventError } = useResource(queryThinEvent(eventId));
  const { data: tickets, error: ticketsError } = useResources(queryEventTicketForUser(eventId, user?.id));

  const isEventLoading = !event && !eventError;

  const isTicketsLoading = user && !tickets && !ticketsError;

  if (eventError) {
    return <EventTicketPromptSkeleton {...props} />;
  }

  if (isEventLoading || isTicketsLoading) {
    return <EventTicketPromptSkeleton {...props} />;
  }

  switch (event?.registrationType) {
    case EventRegistrationType.SelfRegistration:
      return <SelfRegistered eventId={eventId} {...props} />;
    case EventRegistrationType.RegistrationFee:
      return <RegistrationFee eventId={eventId} {...props} />;
    case EventRegistrationType.ExternalAAPT:
      return <ExternalAAPT eventId={eventId} {...props} />;
    case EventRegistrationType.ExternalAAA:
      return <ExternalAAA eventId={eventId} {...props} />;
    default:
      return <SelfRegistered eventId={eventId} />;
  }
};

export const EventTicketPromptSkeleton: FC = (props) => (
  <Box shadow="card" borderRadius={2} p={4} h={234} {...props}>
    <Skeleton w="full" h={8} mb={4} />
    <SkeletonText noOfLines={3} skeletonHeight="4" spacing="2" py="1" mb="auto" />
  </Box>
);
